import React, { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import Header from "./Header.tsx";
import Sidebar from "./Sidebar.tsx";
import FleetMonitor from "./FleetMonitor.tsx";
import styles from "./MultiMonitor.module.css";
import useTheme from "../hooks/useTheme.ts";
import { sidebarOpenForViewport } from "../context/SidebarProvider.tsx";

interface ColumnConfig {
  key: string;
  label: string;
  field: string;
  removable: boolean;
}

const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: "pump_name", label: "Unit", field: "pump_name", removable: false },
  { key: "station", label: "Stn", field: "station", removable: true },
  { key: "gear", label: "Gear", field: "gear", removable: true },
  { key: "throttle", label: "Thrtl", field: "throttle", removable: true },
  { key: "engine_load", label: "Load", field: "engine_load", removable: true },
  { key: "rpm", label: "RPM", field: "rpm", removable: true },
];

interface ActiveStage {
  id: number;
  fleet: number;
  pad_id: string;
  well_id: string;
  stage_id: number;
  major_stage_id_jdd: string;
  wbs: string;
  start_ts: string;
  elapsed: number;
  last_updated: string;
}

interface FleetGroup {
  fleet: number;
  latestPad: {
    pad_name: string;
    start_ts: number | null;
  };
}

interface PumpData {
  pump_name: string;
  station: number;
  gear: number;
  throttle: number;
  engine_load: number;
  rpm: number;
  calculated_discharge_rate: number;
  [key: string]: any;
}

interface TreatmentData {
  clean_total_meas: number;
  slurry_total: number;
  slurry_total_calc: number;
  sand_rate_surface: number;
  density_surface: number;
  treating_pressure: number;
  hp: number;
  pump_timestamp?: number;
  [key: string]: any;
}

interface CustomerWellResult {
  fleet_id: number;
  pad: {
    id: number;
    job_number: number;
    pad_name: string;
    well_count: number;
    stage_count: number;
    start_ts: number;
    end_ts: number | null;
    status: string;
  } | null;
  organization: {
    id: number;
    name: string;
  } | null;
  well: {
    id: number;
    well_name: string;
    api: string;
  } | null;
  stage: {
    id: number;
    stage_number: number;
    start_ts: number;
  } | null;
  reason: string;
}

interface CustomerWellData {
  fleet_count: number;
  results: CustomerWellResult[];
}

interface PumpConfig {
  pump_name: string;
  van_id: string;
  engine_type: string;
  trans_type: string;
  dgb_type: string;
  [key: string]: any;
}

interface FleetData {
  pump_timestamp: number | null;
  pumps: PumpData[];
  treatment_timestamp?: number;
  treatment: TreatmentData | null;
}

interface MultiMonitorProps {
  embedded?: boolean;
}

const MultiMonitor: React.FC<MultiMonitorProps> = ({ embedded = false }) => {
  const [showSidebar, setShowSidebar] = useState(sidebarOpenForViewport);
  const [fleetGroups, setFleetGroups] = useState<FleetGroup[]>([]);
  const [selectedFleets, setSelectedFleets] = useState<number[]>([]);
  const [availableFleet, setAvailableFleet] = useState<number | null>(null);
  const [fleetDataMap, setFleetDataMap] = useState<Map<number, FleetData>>(
    new Map(),
  );
  const [customerWellDataMap, setCustomerWellDataMap] = useState<
    Map<number, CustomerWellResult>
  >(new Map());
  const [vanIdMap, setVanIdMap] = useState<Map<number, string>>(new Map());
  const [pumpConfigsMap, setPumpConfigsMap] = useState<
    Map<number, Map<string, PumpConfig>>
  >(new Map());
  const [automatedView, setAutomatedView] = useState(false);
  const [columnsByFleet, setColumnsByFleet] = useState<
    Record<number, ColumnConfig[]>
  >({});

  const getColumnsForFleet = (fleet: number) =>
    columnsByFleet[fleet] || DEFAULT_COLUMNS;

  const handleColumnsChange = (fleet: number, columns: ColumnConfig[]) => {
    setColumnsByFleet((prev) => ({
      ...prev,
      [fleet]: columns,
    }));
  };

  const { theme } = useTheme();
  const backendUrl =
    process.env.REACT_APP_BACKEND_URL || "http://localhost:5000/api";
  const multiFleetDataInterval = useRef<NodeJS.Timeout | null>(null);
  const customerWellInterval = useRef<NodeJS.Timeout | null>(null);
  const hasInitialPumpConfigsFetched = useRef(false);

  const fetchFleets = useCallback(async () => {
    try {
      const token = localStorage.getItem("token");
      const response = await axios.get(`${backendUrl}/active-stages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const activeStages: ActiveStage[] = response.data;

      const fleetMap = new Map();
      activeStages.forEach((stage) => {
        if (stage.fleet) {
          const existing = fleetMap.get(stage.fleet);
          const stageTimestamp = new Date(stage.last_updated).getTime();

          if (!existing || stageTimestamp > existing.latestPad.start_ts) {
            fleetMap.set(stage.fleet, {
              fleet: stage.fleet,
              latestPad: {
                pad_name: stage.pad_id,
                start_ts: stageTimestamp,
              },
            });
          }
        }
      });

      const groups = Array.from(fleetMap.values()).sort(
        (a: FleetGroup, b: FleetGroup) => a.fleet - b.fleet,
      );
      setFleetGroups(groups);

      if (automatedView) {
        const activeFleetIds = Array.from(fleetMap.keys()).sort(
          (a, b) => a - b,
        );
        setSelectedFleets(activeFleetIds);
      } else {
        if (groups.length > 0 && availableFleet === null) {
          setAvailableFleet(groups[0].fleet);
        }
      }
    } catch (error) {
      console.error("Error fetching active fleets:", error);
    }
  }, [backendUrl, availableFleet, automatedView]);

  const fetchMultiFleetData = useCallback(async () => {
    if (selectedFleets.length === 0) return;

    try {
      const token = localStorage.getItem("token");
      const fleetsParam = selectedFleets.join(",");
      const response = await axios.get(
        `${backendUrl}/latest-treatment-pump-data-multi?fleets=${fleetsParam}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data: Record<number, FleetData> = response.data;

      const newDataMap = new Map<number, FleetData>();
      Object.keys(data).forEach((fleetId) => {
        const fleetNum = parseInt(fleetId);
        newDataMap.set(fleetNum, data[fleetNum]);
      });

      setFleetDataMap(newDataMap);
    } catch (error) {
      console.error("Error fetching multi-fleet data:", error);
    }
  }, [selectedFleets, backendUrl]);

  const fetchCustomerWellData = useCallback(async () => {
    if (selectedFleets.length === 0) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${backendUrl}/current_customer_well`,
        {
          fleet_ids: selectedFleets,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const data: CustomerWellData = response.data;

      const newCustomerWellMap = new Map<number, CustomerWellResult>();
      data.results.forEach((result) => {
        newCustomerWellMap.set(result.fleet_id, result);
      });

      setCustomerWellDataMap(newCustomerWellMap);
    } catch (error) {
      console.error("Error fetching customer/well data:", error);
    }
  }, [selectedFleets, backendUrl]);

  const fetchPumpConfigs = useCallback(async () => {
    if (selectedFleets.length === 0) return;

    // Collect all pump names from all fleets
    const allPumpNames: string[] = [];
    fleetDataMap.forEach((fleetData) => {
      fleetData.pumps.forEach((pump) => {
        allPumpNames.push(pump.pump_name);
      });
    });

    if (allPumpNames.length === 0) return;

    try {
      const token = localStorage.getItem("token");
      const response = await axios.post(
        `${backendUrl}/pump-config/latest`,
        {
          pump_names: allPumpNames,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );

      const configs = response.data.configs || [];

      const fleetVanIdMap = new Map<number, Map<string, number>>();
      const newPumpConfigsMap = new Map<number, Map<string, PumpConfig>>();

      configs.forEach((config: PumpConfig) => {
        fleetDataMap.forEach((fleetData, fleetId) => {
          const pumpInFleet = fleetData.pumps.find(
            (p) => p.pump_name === config.pump_name,
          );
          if (pumpInFleet) {
            if (!newPumpConfigsMap.has(fleetId)) {
              newPumpConfigsMap.set(fleetId, new Map());
            }
            newPumpConfigsMap.get(fleetId)!.set(config.pump_name, config);

            if (config.van_id) {
              if (!fleetVanIdMap.has(fleetId)) {
                fleetVanIdMap.set(fleetId, new Map());
              }
              const vanIdCounts = fleetVanIdMap.get(fleetId)!;
              const count = vanIdCounts.get(config.van_id) || 0;
              vanIdCounts.set(config.van_id, count + 1);
            }
          }
        });
      });

      const newVanIdMap = new Map<number, string>();
      fleetVanIdMap.forEach((vanIdCounts, fleetId) => {
        let mostCommonVanId: string | null = null;
        let maxCount = 0;
        vanIdCounts.forEach((count, vanId) => {
          if (count > maxCount) {
            maxCount = count;
            mostCommonVanId = vanId;
          }
        });
        if (mostCommonVanId) {
          newVanIdMap.set(fleetId, mostCommonVanId);
        }
      });

      setPumpConfigsMap(newPumpConfigsMap);
      setVanIdMap(newVanIdMap);
    } catch (error) {
      console.error("Error fetching pump configs:", error);
    }
  }, [selectedFleets, fleetDataMap, backendUrl]);

  useEffect(() => {
    fetchFleets();

    const pollInterval = setInterval(() => {
      fetchFleets();
    }, 60 * 1000);

    return () => clearInterval(pollInterval);
  }, [fetchFleets]);

  useEffect(() => {
    if (multiFleetDataInterval.current) {
      clearInterval(multiFleetDataInterval.current);
      multiFleetDataInterval.current = null;
    }
    if (customerWellInterval.current) {
      clearInterval(customerWellInterval.current);
      customerWellInterval.current = null;
    }

    hasInitialPumpConfigsFetched.current = false;

    if (selectedFleets.length > 0) {
      fetchMultiFleetData();
      fetchCustomerWellData();

      multiFleetDataInterval.current = setInterval(() => {
        fetchMultiFleetData();
      }, 1000);

      customerWellInterval.current = setInterval(() => {
        fetchCustomerWellData();
        fetchPumpConfigs();
      }, 60000);
    }

    return () => {
      if (multiFleetDataInterval.current) {
        clearInterval(multiFleetDataInterval.current);
        multiFleetDataInterval.current = null;
      }
      if (customerWellInterval.current) {
        clearInterval(customerWellInterval.current);
        customerWellInterval.current = null;
      }
    };
  }, [selectedFleets]);

  useEffect(() => {
    if (selectedFleets.length === 0) return;

    if (hasInitialPumpConfigsFetched.current) return;

    const hasPumps = Array.from(fleetDataMap.values()).some(
      (fleetData) => fleetData.pumps && fleetData.pumps.length > 0,
    );

    if (hasPumps) {
      fetchPumpConfigs();
      hasInitialPumpConfigsFetched.current = true;
    }
  }, [fleetDataMap, selectedFleets, fetchPumpConfigs]);

  const addFleet = () => {
    if (!availableFleet) return;

    setSelectedFleets((prev) => {
      if (prev.includes(availableFleet)) return prev;

      const updatedFleets = [...prev, availableFleet];

      const remaining = fleetGroups.filter(
        (g) => !updatedFleets.includes(g.fleet),
      );

      setAvailableFleet(remaining.length > 0 ? remaining[0].fleet : null);

      return updatedFleets;
    });
  };

  const removeFleet = (fleet: number) => {
    setSelectedFleets(selectedFleets.filter((f) => f !== fleet));
    if (availableFleet === null) {
      setAvailableFleet(fleet);
    }
  };

  const extractToWindow = (fleet: number) => {
    const url = `${window.location.origin}/monitor?fleet=${fleet}`;
    const windowFeatures =
      "width=1200,height=800,menubar=no,toolbar=no,location=no,status=no";
    window.open(url, `fleet-${fleet}`, windowFeatures);
  };

  const extractAllToWindows = () => {
    selectedFleets.forEach((fleet) => {
      extractToWindow(fleet);
    });
  };

  const toggleAutomatedView = () => {
    const newAutomatedViewState = !automatedView;
    setAutomatedView(newAutomatedViewState);

    if (newAutomatedViewState) {
      fetchFleets();
    } else {
      setSelectedFleets([]);
    }
  };

  return embedded ? (
    <div className={styles.embeddedContainer}>
      <div className={styles.controls}>
        <div className={styles.addFleetSection}>
          <select
            value={availableFleet || ""}
            onChange={(e) => setAvailableFleet(parseInt(e.target.value))}
            className={styles.fleetSelect}
            disabled={!availableFleet || automatedView}
          >
            <option value="" disabled>
              No more fleets
            </option>
            {fleetGroups
              .filter((g) => !selectedFleets.includes(g.fleet))
              .map((group) => (
                <option key={group.fleet} value={group.fleet}>
                  Fleet {group.fleet}
                </option>
              ))}
          </select>
          <button
            onClick={addFleet}
            className={styles.addButton}
            disabled={!availableFleet || automatedView}
          >
            + Add Fleet
          </button>
        </div>

        <button
          onClick={toggleAutomatedView}
          className={`${styles.automatedViewButton} ${automatedView ? styles.active : ""}`}
        >
          <div className={styles.toggleSwitch}>
            <div
              className={`${styles.toggleSlider} ${automatedView ? styles.sliderActive : ""}`}
            ></div>
          </div>
          <span>Automated View</span>
        </button>
      </div>

      {selectedFleets.length === 0 ? (
        <div className={styles.emptyState}>
          <svg
            className={styles.emptyIcon}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
          >
            <rect
              x="3"
              y="3"
              width="18"
              height="18"
              rx="2"
              ry="2"
              strokeWidth="2"
            />
            <line x1="9" y1="3" x2="9" y2="21" strokeWidth="2" />
            <line x1="15" y1="9" x2="15" y2="15" strokeWidth="2" />
            <line x1="3" y1="9" x2="21" y2="9" strokeWidth="2" />
          </svg>
          <h2>No Fleets Added</h2>
          <p>
            Select a fleet from the dropdown above and click "Add Fleet" to
            start monitoring
          </p>
        </div>
      ) : (
        <div className={styles.gridContainer}>
          {selectedFleets.map((fleet) => (
            <div key={fleet} className={styles.fleetPanel}>
              <div className={styles.panelHeader}>
                <h3 className={styles.panelTitle}>Fleet {fleet}</h3>
                <div className={styles.panelActions}>
                  <button
                    onClick={() => extractToWindow(fleet)}
                    className={styles.extractButton}
                    title="Open in new window"
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  </button>
                  <button
                    onClick={() => removeFleet(fleet)}
                    className={styles.removeButton}
                    title="Remove from dashboard"
                    disabled={automatedView}
                  >
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <line x1="18" y1="6" x2="6" y2="18" />
                      <line x1="6" y1="6" x2="18" y2="18" />
                    </svg>
                  </button>
                </div>
              </div>
              <div className={styles.monitorWrapper}>
                <FleetMonitor
                  fleet={fleet}
                  externalPumps={fleetDataMap.get(fleet)?.pumps}
                  externalTreatment={fleetDataMap.get(fleet)?.treatment ?? null}
                  externalPumpTimestamp={
                    fleetDataMap.get(fleet)?.pump_timestamp ?? null
                  }
                  externalCustomerWell={
                    customerWellDataMap.has(fleet)
                      ? {
                          fleet_count: 1,
                          results: [customerWellDataMap.get(fleet)!],
                        }
                      : null
                  }
                  externalVanId={vanIdMap.get(fleet) ?? null}
                  externalPumpConfigs={pumpConfigsMap.get(fleet)}
                  useExternalData={true}
                  visibleColumns={getColumnsForFleet(fleet)}
                  onColumnsChange={(columns) =>
                    handleColumnsChange(fleet, columns)
                  }
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  ) : (
    <section
      className={`${styles.section} ${theme === "dark" ? "dark-theme" : "light-theme"}`}
    >
      <div className={styles.profile}>
        <Header
          title="Multi-Monitor Dashboard"
          setShowSidebar={setShowSidebar}
          showSidebar={showSidebar}
        />

        <div className={styles.container}>
          <div
            className={`${styles.sidebarStyle} app-sidebar-rail`}
            style={{
              width: showSidebar ? "224px" : "56px",
              transition: "width 0.3s",
            }}
          >
            <Sidebar />
          </div>

          <div className={styles.mainContentStyle}>
            <div className={styles.controls}>
              <div className={styles.addFleetSection}>
                <select
                  value={availableFleet || ""}
                  onChange={(e) => setAvailableFleet(parseInt(e.target.value))}
                  className={styles.fleetSelect}
                  disabled={!availableFleet || automatedView}
                >
                  <option value="" disabled>
                    No more fleets
                  </option>
                  {fleetGroups
                    .filter((g) => !selectedFleets.includes(g.fleet))
                    .map((group) => (
                      <option key={group.fleet} value={group.fleet}>
                        Fleet {group.fleet}
                      </option>
                    ))}
                </select>
                <button
                  onClick={addFleet}
                  className={styles.addButton}
                  disabled={!availableFleet || automatedView}
                >
                  + Add Fleet
                </button>
              </div>

              <button
                onClick={toggleAutomatedView}
                className={`${styles.automatedViewButton} ${automatedView ? styles.active : ""}`}
              >
                <div className={styles.toggleSwitch}>
                  <div
                    className={`${styles.toggleSlider} ${automatedView ? styles.sliderActive : ""}`}
                  ></div>
                </div>
                <span>Automated View</span>
              </button>
            </div>

            {selectedFleets.length === 0 ? (
              <div className={styles.emptyState}>
                <svg
                  className={styles.emptyIcon}
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                >
                  <rect
                    x="3"
                    y="3"
                    width="18"
                    height="18"
                    rx="2"
                    ry="2"
                    strokeWidth="2"
                  />
                  <line x1="9" y1="3" x2="9" y2="21" strokeWidth="2" />
                  <line x1="15" y1="9" x2="15" y2="15" strokeWidth="2" />
                  <line x1="3" y1="9" x2="21" y2="9" strokeWidth="2" />
                </svg>
                <h2>No Fleets Added</h2>
                <p>
                  Select a fleet from the dropdown above and click "Add Fleet"
                  to start monitoring
                </p>
              </div>
            ) : (
              <div className={styles.gridContainer}>
                {selectedFleets.map((fleet) => (
                  <div key={fleet} className={styles.fleetPanel}>
                    <div className={styles.panelHeader}>
                      <h3 className={styles.panelTitle}>Fleet {fleet}</h3>
                      <div className={styles.panelActions}>
                        <button
                          onClick={() => extractToWindow(fleet)}
                          className={styles.extractButton}
                          title="Open in new window"
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                            <polyline points="15 3 21 3 21 9" />
                            <line x1="10" y1="14" x2="21" y2="3" />
                          </svg>
                        </button>
                        <button
                          onClick={() => removeFleet(fleet)}
                          className={styles.removeButton}
                          title="Remove from dashboard"
                          disabled={automatedView}
                        >
                          <svg
                            width="16"
                            height="16"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                          >
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                    </div>
                    <div className={styles.monitorWrapper}>
                      <FleetMonitor
                        fleet={fleet}
                        externalPumps={fleetDataMap.get(fleet)?.pumps}
                        externalTreatment={
                          fleetDataMap.get(fleet)?.treatment ?? null
                        }
                        externalPumpTimestamp={
                          fleetDataMap.get(fleet)?.pump_timestamp ?? null
                        }
                        externalCustomerWell={
                          customerWellDataMap.has(fleet)
                            ? {
                                fleet_count: 1,
                                results: [customerWellDataMap.get(fleet)!],
                              }
                            : null
                        }
                        externalVanId={vanIdMap.get(fleet) ?? null}
                        externalPumpConfigs={pumpConfigsMap.get(fleet)}
                        useExternalData={true}
                        visibleColumns={getColumnsForFleet(fleet)}
                        onColumnsChange={(columns) =>
                          handleColumnsChange(fleet, columns)
                        }
                      />
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default MultiMonitor;
